import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import { queryKeys } from '@/shared/hooks';

type TaggableTableEntity = 'feature' | 'release' | 'suite' | 'test';

type TaggableTableRow = {
  id: string;
  name: string;
  projectId: string;
};

type DeleteTarget = {
  id: string;
  name: string;
  projectId: string;
};

type DeleteMutation = {
  isPending: boolean;
  mutate: (
    input: { id: string; projectId: string },
    callbacks: { onSuccess: () => void; onError: (error: unknown) => void },
  ) => void;
};

type UseTaggableTableRowActionsParams<TRow extends TaggableTableRow> = {
  deleteMutation: DeleteMutation;
  entity: TaggableTableEntity;
  selectedProjectId: string | null;
  updateRowTags: (input: { row: TRow; tags: string[] }) => Promise<void>;
  updateRowTagsPending: boolean;
};

type UseTaggableTableRowActionsResult<TRow extends TaggableTableRow> = {
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: TRow) => void;
  onUpdateRowTagsColor: (input: { row: TRow; tags: string[]; colors: TagColor[] }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useTaggableTableRowActions = <TRow extends TaggableTableRow>({
  deleteMutation,
  entity,
  selectedProjectId,
  updateRowTags,
  updateRowTagsPending,
}: UseTaggableTableRowActionsParams<TRow>): UseTaggableTableRowActionsResult<TRow> => {
  const queryClient = useQueryClient();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const onDeleteRow = useCallback((row: TRow) => {
    setDeleteTarget({ id: row.id, name: row.name, projectId: row.projectId });
  }, []);

  const onDeleteClose = useCallback(() => {
    if (deleteMutation.isPending) return;
    setDeleteTarget(null);
  }, [deleteMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    const deletedEntityName = deleteTarget.name;
    deleteMutation.mutate(
      { id: deleteTarget.id, projectId: deleteTarget.projectId },
      {
        onSuccess: () => {
          notify({
            message: getToastMessage('entity.deleted', {
              entity,
              name: deletedEntityName,
            }),
            tone: 'green',
          });
          setDeleteTarget(null);
        },
        onError: (error) => {
          notify({ message: toErrorMessage(error), tone: 'red' });
        },
      },
    );
  }, [deleteMutation, deleteTarget, entity, notify, toErrorMessage]);

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: TRow; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateRowTags({ row, tags: normalizedTags });
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          notify({ message: getToastMessage('tags.updated'), tone: 'green' });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
          });
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateRowTags,
      updateTagColorMutation,
    ],
  );

  return {
    deleteLoading: deleteMutation.isPending,
    deleteRowPendingId: deleteMutation.isPending ? (deleteTarget?.id ?? null) : null,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId:
      updateTagColorMutation.isPending || updateRowTagsPending ? updatingTagRowId : null,
  };
};
